import React, { useRef, useState } from "react";
import { Film, Upload, Trash2, Youtube } from "lucide-react";
import { toast } from "sonner";
import { musicApi, uploadProjectorVideo } from "./musicApi";

const mb = (n) => `${Math.round(n / 1048576)} MB`;

export function ProjectorVideo({ p, onChanged }) {
  const [progress, setProgress] = useState(null);
  const [err, setErr] = useState(null);
  const fileRef = useRef(null);
  const setSource = (source) => musicApi.setProjector({ source }).then((d) => { onChanged(d); toast.success(source === "video" ? "Userò il video caricato" : "Userò il link YouTube"); });
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 600 * 1048576) { toast.error("Video troppo grande (max 600 MB): riducilo a 720p o accorcialo"); return; }
    setProgress(0);
    setErr(null);
    try {
      await uploadProjectorVideo(f, setProgress);
      toast.success("Video caricato: il notebook lo scarica e lo copia sul box");
      musicApi.projector().then(onChanged);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || (e2?.response ? `errore ${e2.response.status}` : e2?.message) || "Caricamento fallito";
      setErr(`Caricamento interrotto: ${msg}. Riprova senza cambiare pagina.`);
      toast.error(msg);
    } finally { setProgress(null); }
  };
  const remove = () => musicApi.deleteProjectorVideo().then(() => musicApi.projector().then(onChanged));
  const pickLocal = (u) => musicApi.useLocalProjectorVideo({ path: u.path, name: u.name, size: u.size }).then(() => { toast.success("Uso il video dalla chiavetta: il notebook lo copia e lo manda al box"); musicApi.projector().then(onChanged); }).catch((e2) => toast.error(e2?.response?.data?.detail || "Errore"));
  const v = p.video;
  const ready = p.agent?.video_ready;
  const usb = (p.agent?.usb_videos || []).filter((u) => !u.local && !(v && (u.path === v.local_path || (u.size === v.size && u.name === v.name))));
  const Opt = ({ id, icon: Icon, label, testId }) => (
    <button onClick={() => setSource(id)} className={`h-9 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1.5 border ${p.source === id ? "bg-[#4a1c17] text-white border-transparent" : "bg-white border-[#e8dcc8] text-[#7c5a4e]"}`} data-testid={testId}><Icon className="h-3.5 w-3.5" /> {label}</button>
  );
  return (
    <div className="space-y-2" data-testid="projector-video">
      <div className="text-[11px] uppercase tracking-wide text-[#7c5a4e]">Cosa mostra lo schermo</div>
      <div className="flex flex-wrap gap-2">
        <Opt id="video" icon={Film} label="Video caricato (VLC, in loop)" testId="projector-source-video" />
        <Opt id="youtube" icon={Youtube} label="Link YouTube" testId="projector-source-youtube" />
      </div>
      {p.source === "video" && (
        <div className="rounded-lg border border-[#e8dcc8] bg-white p-2.5 text-xs space-y-1.5">
          {v ? (
            <div className="flex items-center gap-2" data-testid="projector-video-info">
              <Film className="h-4 w-4 text-[#2b7a46] shrink-0" />
              <span className="font-semibold truncate">{v.name}</span>
              <span className="text-[#7c5a4e] whitespace-nowrap">{mb(v.size)}</span>
              {ready && <span className={`ml-auto whitespace-nowrap ${ready === "pronto" ? "text-emerald-700" : "text-[#C48B31]"}`} data-testid="projector-video-ready">{ready === "pronto" ? "sul box ✓" : ready}</span>}
              <button onClick={remove} className="text-[#7c5a4e] hover:text-red-600" title="Rimuovi video" data-testid="projector-video-del"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ) : <div className="text-[#7c5a4e] italic">Nessun video: caricane uno (MP4, max 600 MB — 10–30 minuti bastano, va in loop)</div>}
          {err && <div className="text-rose-700 font-semibold" data-testid="projector-video-error">{err}</div>}
          {p.agent?.launch && !p.agent?.video_playing && <div className="text-[11px] text-[#7c5a4e] font-mono break-all" data-testid="projector-launch">Ultimo avvio VLC: {p.agent.launch}</div>}
          {progress != null ? (
            <div className="space-y-1" data-testid="projector-video-progress"><div className="h-2 rounded-full bg-[#f1e7dc] overflow-hidden"><div className="h-full bg-[#2b7a46] transition-[width]" style={{ width: `${progress}%` }} /></div><div className="text-[10px] text-[#7c5a4e]">{progress}% · non cambiare pagina</div></div>
          ) : (
            <button onClick={() => fileRef.current?.click()} className="h-8 px-3 rounded-md bg-[#2b7a46] text-white font-semibold inline-flex items-center gap-1" data-testid="projector-video-upload"><Upload className="h-3.5 w-3.5" /> {v ? "Sostituisci video" : "Carica video"}</button>
          )}
          <input ref={fileRef} type="file" accept="video/mp4,video/*" className="hidden" onChange={pick} data-testid="projector-video-file" />
          {usb.length > 0 && (
            <div className="rounded-md bg-[#fff8ec] border border-[#f1dfb8] p-2 space-y-1" data-testid="projector-usb-videos">
              <div className="font-semibold text-[#8a5a12]">Video trovati sulla chiavetta USB nel notebook:</div>
              {usb.map((u) => (
                <div key={u.path} className="flex items-center gap-2">
                  <span className="truncate">{u.name}</span><span className="text-[#7c5a4e] whitespace-nowrap">{mb(u.size)}</span>
                  <button onClick={() => pickLocal(u)} className="ml-auto h-7 px-2 rounded-md bg-[#4a1c17] text-white font-semibold whitespace-nowrap" data-testid={`projector-usb-use-${u.name}`}>Usa questo</button>
                </div>
              ))}
            </div>
          )}
          {p.agent?.connected && p.agent?.vlc_installed === false && <div className="text-rose-700 font-semibold" data-testid="projector-vlc-missing">Sul box manca VLC: installalo dal Play Store (gratuito), poi premi Accendi</div>}
        </div>
      )}
    </div>
  );
}
